import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/Button";
import { Dialog, DialogTitle, DialogDescription } from "./ui/Dialog";
import { api } from "../lib/api";
import { useSDK } from "../lib/sdk-context";
import { displayLicenseName } from "../lib/display-name";
import type { ChainCandidate } from "./ChainCandidateCard";

interface SearchRow {
  license_number: string;
  legal_business_name: string | null;
  primary_dba: string | null;
  display_name: string | null;
  license_type: string | null;
  premise_city: string | null;
  license_status: string | null;
  chain_id: string | null;
  chain_display_name: string | null;
  candidate_id: string | null;
  candidate_root: string | null;
}

interface StagedRow {
  license_number: string;
  legal_business_name: string | null;
  primary_dba: string | null;
  display_name: string | null;
  premise_city: string | null;
  license_type: string | null;
  alias_checked: boolean;
  current_chain_id: string | null;
  current_chain_name: string | null;
  current_candidate_id: string | null;
  current_candidate_root: string | null;
}

interface Conflict {
  license_number: string;
  primary_dba: string | null;
  display_name: string | null;
  currently_on_chain_id: string | null;
  currently_on_chain_name: string | null;
  currently_on_candidate_id: string | null;
  currently_on_candidate_root: string | null;
}

interface Props {
  open: boolean;
  candidate: ChainCandidate;
  onClose: () => void;
  onAdded: () => void;
}

const SUFFIX_RX = /\b(dispensary|delivery|cannabis|llc|inc|co|corp|company|holdings|group|the|a|of|and|&)\b/gi;
function nameStem(s: string): string {
  return (s || "")
    .toLowerCase()
    .replace(SUFFIX_RX, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
function aliasDefaultFor(licenseName: string, candidateRoot: string): boolean {
  const sStem = nameStem(licenseName);
  const cStem = nameStem(candidateRoot);
  if (!sStem || !cStem) return true;
  return !sStem.includes(cStem) && !cStem.includes(sStem);
}

export default function AddLocationDialog({ open, candidate, onClose, onAdded }: Props) {
  const { context } = useSDK();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<SearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [staged, setStaged] = useState<StagedRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Manual GBP-link via pasted Google Maps share URL.
  const [shareUrl, setShareUrl] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const [linkMsg, setLinkMsg] = useState<string | null>(null);
  // When a link attempt returns no_license_match, prompt to create a shadow row.
  const [shadowPrompt, setShadowPrompt] = useState<{ place_name: string | null } | null>(null);

  // Reset state every time the dialog re-opens.
  useEffect(() => {
    if (!open) return;
    setQ("");
    setRows([]);
    setStaged([]);
    setBusy(false);
    setConflicts(null);
    setSearchErr(null);
    setSubmitErr(null);
    setShareUrl("");
    setLinkBusy(false);
    setLinkErr(null);
    setLinkMsg(null);
    setShadowPrompt(null);
  }, [open]);

  async function linkGbp(opts?: { forceCreateShadow?: boolean }) {
    const url = shareUrl.trim();
    if (!url) return;
    setLinkBusy(true);
    setLinkErr(null);
    setLinkMsg(null);
    try {
      const res = await fetch(`/api/admin/chains/candidates/${candidate.candidate_id}/link-gbp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-heady-user-id": context?.userId || "standalone-user",
          "x-heady-team-id": context?.teamId || "standalone-team",
        },
        body: JSON.stringify({
          share_url: url,
          alias_action: "add",
          force_create_shadow: opts?.forceCreateShadow === true,
          dry_run: false,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok) {
        setShadowPrompt(null);
        const where = j?.shadow ? "shadow entry" : "matched license";
        setLinkMsg(`Linked GBP "${j?.place?.display_name ?? ""}" as ${where}.`);
        onAdded();
        return;
      }
      if (res.status === 404 && j?.error === "no_license_match") {
        setShadowPrompt({ place_name: j?.place?.display_name ?? null });
        return;
      }
      if (res.status === 409 && j?.error === "ambiguous_match") {
        const names = (j?.candidates || [])
          .map((c: any) => `${c.display_name || c.license_number} (${Math.round((c.score || 0) * 100)}%)`)
          .join(", ");
        setLinkErr(`Multiple low-confidence matches. Add the exact license from search instead: ${names}`);
        return;
      }
      setLinkErr(j?.message || j?.error || `${res.status} ${res.statusText}`);
    } catch (e) {
      setLinkErr((e as Error).message);
    } finally {
      setLinkBusy(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setRows([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    setSearchErr(null);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const r = await api.get(
          context,
          `/api/admin/chains/license-search?q=${encodeURIComponent(q.trim())}&limit=20`
        );
        setRows(r.rows || []);
      } catch (e) {
        setSearchErr((e as Error).message);
        setRows([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q, context, open]);

  const stagedByLicense = useMemo(() => {
    const m = new Map<string, StagedRow>();
    for (const s of staged) m.set(s.license_number, s);
    return m;
  }, [staged]);

  function stageRow(row: SearchRow) {
    if (stagedByLicense.has(row.license_number)) return;
    const name =
      displayLicenseName(row.display_name, row.primary_dba, row.legal_business_name) || "";
    const aliasChecked = name ? aliasDefaultFor(name, candidate.canonical_root) : false;
    setStaged((prev) => [
      ...prev,
      {
        license_number: row.license_number,
        legal_business_name: row.legal_business_name,
        primary_dba: row.primary_dba,
        display_name: row.display_name,
        premise_city: row.premise_city,
        license_type: row.license_type,
        alias_checked: aliasChecked,
        current_chain_id: row.chain_id,
        current_chain_name: row.chain_display_name,
        current_candidate_id:
          row.candidate_id && row.candidate_id !== candidate.candidate_id ? row.candidate_id : null,
        current_candidate_root:
          row.candidate_id && row.candidate_id !== candidate.candidate_id ? row.candidate_root : null,
      },
    ]);
  }

  function unstage(licenseNumber: string) {
    setStaged((prev) => prev.filter((s) => s.license_number !== licenseNumber));
  }

  function toggleAlias(licenseNumber: string, value: boolean) {
    setStaged((prev) =>
      prev.map((s) => (s.license_number === licenseNumber ? { ...s, alias_checked: value } : s))
    );
  }

  async function submitBatch(opts?: {
    forceMoveLicenseNumbers?: string[];
    skipLicenseNumbers?: string[];
  }) {
    if (staged.length === 0) return;
    setBusy(true);
    setSubmitErr(null);
    try {
      // Use raw fetch so we can read the conflict body on 409.
      const res = await fetch(
        `/api/admin/chains/candidates/${candidate.candidate_id}/add-licenses-batch`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-heady-user-id": context?.userId || "standalone-user",
            "x-heady-team-id": context?.teamId || "standalone-team",
          },
          body: JSON.stringify({
            items: staged.map((s) => ({
              license_number: s.license_number,
              alias_action: s.alias_checked ? "add" : "skip",
            })),
            force_move_license_numbers: opts?.forceMoveLicenseNumbers ?? [],
            skip_license_numbers: opts?.skipLicenseNumbers ?? [],
          }),
        }
      );
      const j = await res.json().catch(() => ({}));
      if (res.status === 409 && j?.error === "already_assigned_batch") {
        setConflicts(j.conflicts || []);
        return;
      }
      if (!res.ok) {
        setSubmitErr(j?.message || j?.error || `${res.status} ${res.statusText}`);
        return;
      }
      onAdded();
      onClose();
    } catch (e) {
      setSubmitErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function onMoveAll() {
    if (!conflicts) return;
    const lns = conflicts.map((c) => c.license_number);
    setConflicts(null);
    void submitBatch({ forceMoveLicenseNumbers: lns });
  }

  function onSkipConflicts() {
    if (!conflicts) return;
    const lns = conflicts.map((c) => c.license_number);
    // Drop them from the tray since the user chose to skip them.
    setStaged((prev) => prev.filter((s) => !lns.includes(s.license_number)));
    setConflicts(null);
    void submitBatch({ skipLicenseNumbers: lns });
  }

  function onCancelConflicts() {
    setConflicts(null);
  }

  function clearTray() {
    setStaged([]);
  }

  const targetMembers = useMemo(
    () => new Set(candidate.member_license_numbers || []),
    [candidate.member_license_numbers]
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogTitle>
        Add locations to <span className="text-accent-700">{candidate.candidate_display_name}</span>
      </DialogTitle>
      <DialogDescription>
        Search for active retail licenses, stage them in the tray, then add the whole batch in one go.
      </DialogDescription>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT: search + results */}
        <div className="space-y-3 min-w-0">
          <input
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by license number, legal name, or DBA…"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            disabled={busy}
          />
          {searching && <p className="text-xs text-gray-500">Searching…</p>}
          {searchErr && <p className="text-xs text-red-700">Search failed: {searchErr}</p>}
          {!searching && q.trim().length >= 2 && rows.length === 0 && !searchErr && (
            <p className="text-xs text-gray-500">No active C9/C10/C12 licenses match.</p>
          )}
          {q.trim().length < 2 && (
            <p className="text-xs text-gray-500">Type at least 2 characters.</p>
          )}

          {/* Manual GBP link via pasted Google Maps share URL. */}
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 space-y-2">
            <label className="block text-xs font-semibold text-gray-700">
              Or paste a Google Maps share URL
            </label>
            <textarea
              value={shareUrl}
              onChange={(e) => {
                setShareUrl(e.target.value);
                setShadowPrompt(null);
                setLinkErr(null);
                setLinkMsg(null);
              }}
              rows={2}
              placeholder="https://maps.app.goo.gl/…  or  https://www.google.com/maps/place/…"
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
              disabled={linkBusy || busy}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                type="button"
                onClick={() => linkGbp()}
                disabled={linkBusy || busy || shareUrl.trim().length === 0}
              >
                {linkBusy ? "Resolving…" : "Resolve & link"}
              </Button>
              {linkMsg && <span className="text-xs text-emerald-700">{linkMsg}</span>}
            </div>
            {linkErr && <p className="text-xs text-red-700">{linkErr}</p>}
            {shadowPrompt && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 space-y-2">
                <p>
                  No CA license matched this address
                  {shadowPrompt.place_name ? ` for "${shadowPrompt.place_name}"` : ""}. Create a
                  shadow license entry so we can still track this store?
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => linkGbp({ forceCreateShadow: true })}
                    disabled={linkBusy}
                  >
                    {linkBusy ? "Creating…" : "Create shadow entry"}
                  </Button>
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => setShadowPrompt(null)}
                    disabled={linkBusy}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 rounded-md border border-gray-200">
            {rows.map((row) => {
              const isMember = targetMembers.has(row.license_number);
              const isStaged = stagedByLicense.has(row.license_number);
              const onChain = !!row.chain_id;
              const otherCandidate =
                !!row.candidate_id && row.candidate_id !== candidate.candidate_id;
              const name =
                displayLicenseName(row.display_name, row.primary_dba, row.legal_business_name) ||
                row.license_number;
              return (
                <li key={row.license_number}>
                  <button
                    type="button"
                    disabled={isMember}
                    onClick={() => {
                      if (isMember) return;
                      if (isStaged) unstage(row.license_number);
                      else stageRow(row);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${
                      isMember ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={isStaged || isMember}
                      disabled={isMember}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-medium text-gray-900">{name}</span>
                        <span className="text-xs text-gray-400">{row.license_number}</span>
                        {row.license_type && (
                          <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                            {row.license_type}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span>{row.premise_city || "—"}</span>
                        {isMember && (
                          <span className="rounded-full border border-accent-200 bg-accent-50 px-2 py-0.5 text-[10px] font-medium text-accent-700">
                            Already a member
                          </span>
                        )}
                        {isStaged && !isMember && (
                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                            ✓ in tray
                          </span>
                        )}
                        {onChain && (
                          <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">
                            On chain: {row.chain_display_name || "unknown"}
                          </span>
                        )}
                        {otherCandidate && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                            On candidate: {row.candidate_root}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* RIGHT: tray */}
        <div className="space-y-3 min-w-0">
          <div className="flex items-baseline justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Staged ({staged.length})</h3>
            {staged.length > 0 && (
              <button
                type="button"
                onClick={clearTray}
                className="text-xs text-gray-500 hover:text-gray-700"
                disabled={busy}
              >
                Clear tray
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200">
            {staged.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-gray-500">
                Search for licenses on the left and add them to the tray.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {staged.map((s) => {
                  const aliasName =
                    displayLicenseName(s.display_name, s.primary_dba, s.legal_business_name) || "";
                  return (
                    <li key={s.license_number} className="px-3 py-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-medium text-gray-900">
                              {displayLicenseName(
                                s.display_name,
                                s.primary_dba,
                                s.legal_business_name
                              ) || s.license_number}
                            </span>
                            <span className="text-xs text-gray-400">{s.license_number}</span>
                            {s.license_type && (
                              <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                                {s.license_type}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-xs text-gray-500">{s.premise_city || "—"}</div>
                          {(s.current_chain_id || s.current_candidate_id) && (
                            <div className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 inline-block">
                              currently on{" "}
                              {s.current_chain_name
                                ? `chain "${s.current_chain_name}"`
                                : `candidate "${s.current_candidate_root}"`}
                            </div>
                          )}
                          {aliasName && (
                            <label className="mt-1 flex items-center gap-1.5 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                checked={s.alias_checked}
                                onChange={(e) => toggleAlias(s.license_number, e.target.checked)}
                                disabled={busy}
                              />
                              <span
                                title="Future detection runs and matcher rules will recognize this name as part of the chain."
                              >
                                Add as alias
                              </span>
                            </label>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => unstage(s.license_number)}
                          className="text-gray-400 hover:text-gray-700 text-sm leading-none px-1"
                          aria-label="Remove from tray"
                          disabled={busy}
                        >
                          ✕
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {submitErr && <p className="mt-3 text-xs text-red-700">{submitErr}</p>}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={() => submitBatch()}
          disabled={busy || staged.length === 0}
        >
          {busy
            ? "Adding…"
            : staged.length === 0
            ? "Add to chain"
            : `Add ${staged.length} to chain`}
        </Button>
      </div>

      {conflicts && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5">
            <h3 className="text-sm font-semibold text-gray-900">
              {conflicts.length} {conflicts.length === 1 ? "license is" : "licenses are"} already
              assigned
            </h3>
            <p className="mt-1 text-xs text-gray-600">
              These licenses are currently on another chain or candidate. Choose how to proceed:
            </p>
            <ul className="mt-3 max-h-48 overflow-y-auto divide-y divide-gray-100 rounded border border-gray-200 text-xs">
              {conflicts.map((c) => (
                <li key={c.license_number} className="px-3 py-2">
                  <div className="font-medium text-gray-900">
                    {displayLicenseName(c.display_name, c.primary_dba, null) || c.license_number}
                  </div>
                  <div className="text-gray-500">
                    {c.license_number} · currently on{" "}
                    {c.currently_on_chain_name
                      ? `chain "${c.currently_on_chain_name}"`
                      : `candidate "${c.currently_on_candidate_root}"`}
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onCancelConflicts} disabled={busy}>
                Cancel
              </Button>
              <Button variant="ghost" type="button" onClick={onSkipConflicts} disabled={busy}>
                Skip conflicts
              </Button>
              <Button variant="primary" type="button" onClick={onMoveAll} disabled={busy}>
                Move all
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
